import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Gift, Users, Sparkle, Clock, CheckCircle, LogIn, Loader2 } from "lucide-react";
import { useWaitlistStore } from "@/stores/useWaitlistStore";
import { toast } from "sonner";

interface WaitlistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WaitlistDialog({ open, onOpenChange }: WaitlistDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const {
    remainingSpots,
    decrementSpots,
    addToWaitlist,
    authUser,
    authChecked,
    setAuthUser,
    setAuthChecked,
    isUserWaitlisted,
    waitlistedEmail,
  } = useWaitlistStore();

  useEffect(() => {
    if (open) {
      checkAuth();
    }
  }, [open]);

  const checkAuth = async () => {
    setIsCheckingAuth(true);
    try {
      const response = await fetch('/api/auth/me');
      const data = await response.json();
      if (data.authenticated && data.user) {
        setAuthUser(data.user);
      } else {
        setAuthUser(null);
      }
    } catch {
      setAuthUser(null);
    } finally {
      setAuthChecked(true);
      setIsCheckingAuth(false);
    }
  };

  const handleSignIn = () => {
    window.location.href = '/__replauthuser';
  };

  const handleSubmit = async () => {
    if (!authUser) return;

    if (isUserWaitlisted && waitlistedEmail === authUser.email) {
      toast.info("You're already on the Plus waitlist!", {
        description: "We'll notify you when it's time to upgrade."
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (response.status === 409 && data.alreadyWaitlisted) {
        toast.info("You're already on the waitlist!", {
          description: "We'll notify you when Plus features launch."
        });
        if (!isUserWaitlisted) {
          addToWaitlist(authUser.email);
        }
        setIsSuccess(true);
        setTimeout(() => {
          onOpenChange(false);
          setIsSuccess(false);
        }, 3000);
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || `Server responded with status ${response.status}`);
      }

      decrementSpots();
      addToWaitlist(authUser.email);
      setIsSuccess(true);

      toast.success("You're on the Plus waitlist!", {
        description: data.message || "We'll notify you when Plus features launch.",
      });

      setTimeout(() => {
        onOpenChange(false);
        setIsSuccess(false);
      }, 3000);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Please try again later.';
      toast.error("Failed to join waitlist", {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const alreadyWaitlisted = isUserWaitlisted && authUser && waitlistedEmail === authUser.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-purple-600 text-white border-0 max-w-md">
        {isSuccess ? (
          <div className="text-center py-8">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">You're on the list!</h2>
            <p className="text-white/80 mb-6">Thank you for joining our waitlist. We'll be in touch soon!</p>
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-white text-purple-600 hover:bg-gray-100 rounded-full px-6"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogTitle className="text-2xl font-bold text-white">
              Meduler Plus Access
            </DialogTitle>

            <div className="flex items-center gap-2">
              <div className="rounded-full bg-white w-fit px-4 py-1 text-purple-600 font-semibold">
                Plus Access
              </div>
              <Badge variant="outline" className="bg-white/10 border-white/30 text-white">
                <Clock className="h-3 w-3 mr-1" />
                <span>{remainingSpots} spots left</span>
              </Badge>
            </div>

            <DialogHeader>
              <h2 className="text-xl font-bold text-white">
                Plus Access Benefits
              </h2>
              <DialogDescription className="text-white/80">
                Join our exclusive waitlist to be notified when Plus features launch.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Gift className="h-5 w-5 shrink-0 mt-0.5" />
                  <span>Limited spots for 1 month free Plus perks - only <strong className="text-amber-300">{remainingSpots}</strong> left</span>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 shrink-0 mt-0.5" />
                  <span>Exclusive access to upcoming AI-powered adherence features</span>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 shrink-0 mt-0.5" />
                  <span>Free caregiver account when you sign up today.</span>
                </div>
              </div>

              <div className="space-y-4">
                {isCheckingAuth ? (
                  <div className="flex items-center justify-center py-4 gap-2 text-white/80">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Checking authentication...</span>
                  </div>
                ) : !authUser ? (
                  <div className="space-y-3">
                    <p className="text-white/80 text-sm text-center">
                      Sign in with your Google account to join the waitlist securely.
                    </p>
                    <Button
                      className="w-full bg-white text-purple-700 hover:bg-gray-100 rounded-full h-12 font-semibold flex items-center justify-center gap-2"
                      onClick={handleSignIn}
                      type="button"
                    >
                      <LogIn className="h-5 w-5" />
                      Sign in with Google to Join Waitlist
                    </Button>
                  </div>
                ) : alreadyWaitlisted ? (
                  <div className="w-full py-3 px-4 bg-green-500/30 border border-green-300/40 rounded-lg text-center">
                    <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-200" />
                    <p className="text-white font-medium">You're already on the waitlist!</p>
                    <p className="text-white/70 text-sm mt-1">{authUser.email}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="w-full py-2 px-4 bg-white/10 border border-white/20 rounded-full text-center text-sm text-white/90">
                      Signed in as <strong>{authUser.email}</strong>
                    </div>
                    <Button
                      className="w-full bg-white/20 hover:bg-white/30 border border-white/50 rounded-full h-12 transition-colors"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      type="button"
                    >
                      {isSubmitting ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <Sparkle className="h-4 w-4" />
                          Claim Your Plus Spot ({remainingSpots} left)
                        </span>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
